import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, HeartHandshake, Church, Users, ArrowRight } from "lucide-react";

const pillars = [
  {
    icon: HomeIcon,
    title: "A Concrete Home",
    text: "Joseph House is a physical re-entry residence in Tallahassee — real beds, real meals, and a real address for men walking out of Florida's prisons.",
  },
  {
    icon: HeartHandshake,
    title: "Radical Hospitality",
    text: "Residents are received as brothers, not clients. Accompaniment, not surveillance. Belonging, not processing.",
  },
  {
    icon: Church,
    title: "A Collaborative Ecosystem",
    text: "Supported by Bishop William Wack, local dioceses, residents, and global benefactors working as one body.",
  },
];

const stats = [
  { value: "2018", label: "Joseph House founded in Tallahassee" },
  { value: "3", label: "Countries in the fraternity" },
  { value: "7", label: "Aspiring brothers in Uganda" },
  { value: "∞", label: "Dignity restored" },
];

export default function JosephHouse() {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div data-testid="joseph-house-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-28 sm:py-36 overflow-hidden">
        <div className="absolute inset-0">
          {/* Blurred backdrop for mobile portrait screens */}
          <img
            src="https://images.unsplash.com/photo-1697490251788-21888514f669"
            alt=""
            className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60 md:hidden"
          />
          {/* Crisp image */}
          <img
            src="https://images.unsplash.com/photo-1697490251788-21888514f669"
            alt="Community standing together in social restoration"
            className="absolute inset-0 w-full h-full object-contain md:object-cover"
          />
          <div className="absolute inset-0 bg-brand-ink/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-brand-sand text-sm uppercase tracking-[0.3em] font-semibold mb-5">
            Joseph House &amp; Ministry Impact
          </p>
          <h1 className="font-serif font-light text-brand-bg text-4xl sm:text-6xl leading-tight max-w-3xl">
            Where release becomes homecoming
          </h1>
          <p className="text-brand-bg/80 text-lg mt-6 max-w-2xl leading-relaxed">
            Joseph House turns a conviction into concrete reality — a community and a home for men
            released from Florida's prisons.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 sm:py-28 bg-brand-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="bg-brand-surface border border-brand-line rounded-2xl p-8 hover:shadow-md transition-shadow duration-300"
                data-testid={`impact-pillar-${i}`}
              >
                <div className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center mb-6">
                  <p.icon className="w-6 h-6 text-brand-green" />
                </div>
                <h3 className="font-serif text-2xl text-brand-ink mb-3">{p.title}</h3>
                <p className="text-brand-muted leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-brand-green" data-testid="impact-stats">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-5xl sm:text-6xl text-brand-sand">{s.value}</div>
              <div className="text-brand-bg/75 text-sm mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem narrative */}
      <section className="py-24 sm:py-28 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-brand-terracotta text-sm uppercase tracking-[0.2em] font-semibold mb-4">
            A Body, Not a Building
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-ink leading-tight mb-8">
            Restoration is a shared work
          </h2>
          <div className="flex items-center gap-4 mb-8 text-brand-muted">
            <Users className="w-6 h-6 text-brand-green shrink-0" />
            <p className="leading-relaxed">
              Joseph House exists because many hands hold it up. Founded in 2018 by Fr. Dustin
              Feddon — from years of prison visits begun in 2013 — it is sustained today alongside
              Fr. Yozefu Ssemakula and, with the blessing of Bishop William Wack and the Diocese of
              Pensacola–Tallahassee, has grown into a global fraternity. Residents lend their
              courage and labor. Global benefactors lend the means. Together they form an ecosystem
              where a man's second chance is everyone's shared responsibility.
            </p>
          </div>
          <p className="text-brand-muted leading-relaxed mb-6">
            This is not charity handed down from a height. It is fraternity built across it —
            proof that a community can decide, together, that no one released from prison will walk
            out into nothing.
          </p>
          <Link
            to="/support"
            data-testid="joseph-house-cta"
            className="inline-flex items-center gap-2 bg-brand-green text-brand-bg hover:bg-brand-greendark px-8 py-4 rounded-full font-medium transition-all duration-300"
          >
            Get Involved <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
